import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { DesignConfig, PreviewData, DesignVersion, DesignSuggestion, DesignSuggestionInput, DeviceType, ThemeConfig } from '../types';
import { generateId } from '../utils';
import type { DesignTask } from '../services/api';

export interface DesignState {
  // 当前设计
  designs: DesignConfig[];
  currentDesign: DesignConfig | null;
  
  // 预览状态
  previewData: PreviewData | null;
  previewHtml: string | null;
  isGenerating: boolean;
  previewError: string | null;
  
  // 异步任务状态
  currentTask: DesignTask | null;
  isPolling: boolean;
  pollInterval: number | null;
  
  // 设备与主题
  currentDevice: DeviceType;
  themeConfig: ThemeConfig;
  
  // 版本管理
  versions: DesignVersion[];
  currentVersionId: string | null;
  
  // 调整建议
  suggestions: DesignSuggestion[];
  
  // 可视化AI历史
  visualAIHistory: VisualAIHistoryItem[];
  
  // Actions
  setCurrentDesign: (design: DesignConfig | null) => void;
  addDesign: (design: Omit<DesignConfig, 'id'>) => string;
  updateDesign: (id: string, updates: Partial<DesignConfig>) => void;
  deleteDesign: (id: string) => void;
  
  setPreviewData: (data: PreviewData | null) => void;
  setPreviewHtml: (html: string | null) => void;
  setGenerating: (generating: boolean) => void;
  setPreviewError: (error: string | null) => void;
  refreshPreview: () => void;
  
  // 异步任务Actions
  setCurrentTask: (task: DesignTask | null) => void;
  updateTaskStatus: (task: DesignTask) => void;
  setIsPolling: (isPolling: boolean) => void;
  setPollInterval: (interval: number | null) => void;
  clearTask: () => void;
  
  setCurrentDevice: (device: DeviceType) => void;
  updateThemeConfig: (config: Partial<ThemeConfig>) => void;
  
  addVersion: (version: Omit<DesignVersion, 'id' | 'timestamp'>) => string;
  rollbackToVersion: (versionId: string) => boolean;
  deleteVersion: (versionId: string) => void;
  getVersionById: (versionId: string) => DesignVersion | undefined;
  
  addSuggestion: (suggestion: DesignSuggestionInput) => string;
  applySuggestion: (suggestionId: string) => boolean;
  ignoreSuggestion: (suggestionId: string) => void;
  clearSuggestions: () => void;
  
  addVisualAIHistory: (item: Omit<VisualAIHistoryItem, 'id' | 'timestamp'>) => void;
  clearVisualAIHistory: () => void;
  
  getDesignById: (id: string) => DesignConfig | undefined;
}

export interface VisualAIHistoryItem {
  id: string;
  prompt: string;
  timestamp: number;
  applied: boolean;
}

export const useDesignStore = create<DesignState>()(
  persist(
    (set, get) => ({
      // 初始状态
      designs: [],
      currentDesign: null,
      previewData: null,
      previewHtml: null,
      isGenerating: false,
      previewError: null,
      
      // 异步任务初始状态
      currentTask: null,
      isPolling: false,
      pollInterval: null,
      
      currentDevice: 'desktop',
      themeConfig: {
        primaryColor: '#667eea',
        secondaryColor: '#764ba2',
        backgroundColor: '#ffffff',
        textColor: '#1f2937',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        borderRadius: '8px',
      },
      versions: [],
      currentVersionId: null,
      suggestions: [],
      visualAIHistory: [],

      // 设计管理
      setCurrentDesign: (design) => {
        set({ currentDesign: design });
      },

      addDesign: (design) => {
        const newDesign: DesignConfig = {
          ...design,
          id: generateId(),
          createdAt: Date.now(),
          updatedAt: Date.now(),
        };
        set((state) => ({
          designs: [...state.designs, newDesign],
          currentDesign: newDesign,
        }));
        return newDesign.id;
      },

      updateDesign: (id, updates) => {
        set((state) => ({
          designs: state.designs.map((d) =>
            d.id === id ? { ...d, ...updates, updatedAt: Date.now() } : d
          ),
          currentDesign:
            state.currentDesign?.id === id
              ? { ...state.currentDesign, ...updates, updatedAt: Date.now() }
              : state.currentDesign,
        }));
      },

      deleteDesign: (id) => {
        set((state) => ({
          designs: state.designs.filter((d) => d.id !== id),
          currentDesign:
            state.currentDesign?.id === id ? null : state.currentDesign,
        }));
      },

      // 预览管理
      setPreviewData: (data) => {
        set({ previewData: data });
      },

      setPreviewHtml: (html) => {
        set({ previewHtml: html });
      },

      setGenerating: (generating) => {
        set({ isGenerating: generating });
      },

      setPreviewError: (error) => {
        set({ previewError: error });
      },

      refreshPreview: () => {
        // 触发预览刷新 - 可以通过改变状态通知iframe重新加载
        const { previewHtml } = get();
        if (previewHtml) {
          set({ previewHtml: null });
          setTimeout(() => set({ previewHtml }), 50);
        }
      },

      // 异步任务管理
      setCurrentTask: (task) => {
        set({ currentTask: task });
      },

      updateTaskStatus: (task) => {
        set({ currentTask: task });
        
        // 如果任务完成，更新预览
        if (task.status === 'completed' && task.html) {
          set({ 
            previewHtml: task.html,
            isGenerating: false,
            previewError: null,
          });
        }
        
        // 如果任务失败，设置错误
        if (task.status === 'failed') {
          set({ 
            isGenerating: false,
            previewError: task.error || '生成失败',
          });
        }
      },

      setIsPolling: (isPolling) => {
        set({ isPolling });
      },

      setPollInterval: (interval) => {
        set({ pollInterval: interval });
      },

      clearTask: () => {
        const { pollInterval } = get();
        if (pollInterval) {
          clearInterval(pollInterval);
        }
        set({ 
          currentTask: null, 
          isPolling: false, 
          pollInterval: null,
          isGenerating: false,
        });
      },

      // 设备与主题
      setCurrentDevice: (device) => {
        set({ currentDevice: device });
      },

      updateThemeConfig: (config) => {
        set((state) => ({
          themeConfig: { ...state.themeConfig, ...config },
        }));
      },

      // 版本管理
      addVersion: (version) => {
        const newVersion: DesignVersion = {
          ...version,
          id: generateId(),
          timestamp: Date.now(),
        };
        set((state) => ({
          versions: [...state.versions, newVersion],
          currentVersionId: newVersion.id,
        }));
        return newVersion.id;
      },

      rollbackToVersion: (versionId) => {
        const version = get().versions.find((v) => v.id === versionId);
        if (!version) return false;
        
        set(() => ({
          currentDesign: version.design,
          currentVersionId: versionId,
          previewHtml: version.previewHtml,
        }));
        return true;
      },

      deleteVersion: (versionId) => {
        set((state) => ({
          versions: state.versions.filter((v) => v.id !== versionId),
          currentVersionId: state.currentVersionId === versionId ? null : state.currentVersionId,
        }));
      },

      getVersionById: (versionId) => {
        return get().versions.find((v) => v.id === versionId);
      },

      // 建议管理
      addSuggestion: (suggestion) => {
        const newSuggestion: DesignSuggestion = {
          ...suggestion,
          id: generateId(),
          timestamp: Date.now(),
          status: 'pending',
        };
        set((state) => ({
          suggestions: [...state.suggestions, newSuggestion],
        }));
        return newSuggestion.id;
      },

      applySuggestion: (suggestionId) => {
        const suggestion = get().suggestions.find((s) => s.id === suggestionId);
        if (!suggestion || suggestion.status !== 'pending') return false;
        
        set((state) => ({
          suggestions: state.suggestions.map((s) =>
            s.id === suggestionId ? { ...s, status: 'applied' } : s
          ),
        }));
        return true;
      },

      ignoreSuggestion: (suggestionId) => {
        set((state) => ({
          suggestions: state.suggestions.map((s) =>
            s.id === suggestionId ? { ...s, status: 'ignored' } : s
          ),
        }));
      },

      clearSuggestions: () => {
        set({ suggestions: [] });
      },

      // 可视化AI历史
      addVisualAIHistory: (item) => {
        const newItem: VisualAIHistoryItem = {
          ...item,
          id: generateId(),
          timestamp: Date.now(),
        };
        set((state) => ({
          visualAIHistory: [newItem, ...state.visualAIHistory].slice(0, 50), // 最多保留50条
        }));
      },

      clearVisualAIHistory: () => {
        set({ visualAIHistory: [] });
      },

      getDesignById: (id) => {
        return get().designs.find((d) => d.id === id);
      },
    }),
    {
      name: 'design-storage-v2',
      partialize: (state) => ({ 
        designs: state.designs,
        themeConfig: state.themeConfig,
        versions: state.versions,
        visualAIHistory: state.visualAIHistory,
        // 不持久化任务相关状态
      }),
    }
  )
);
